// PricingRulesTab.js - Master rules that determine which price table to use for each section
import {
  React,
  useEffect,
  useState,
  useMemo,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  useAttributeLabels,
  CONDITION_OPERATORS,
  ACTION_TYPES
} from './PricingCommon';
import PricingRuleDiagram from './PricingRuleDiagram';
import { useProductScope } from '../utils/productScope';

// Single condition row component
const ConditionRow = ({ condition, index, priceTables, attributeLabels, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const [localCond, setLocalCond] = useState(condition);

  useEffect(() => {
    setLocalCond(condition);
  }, [condition]);

  const handleChange = (field, value) => {
    const updated = { ...localCond, [field]: value };
    setLocalCond(updated);
    onUpdate(index, updated);
  };

  const handleConditionFieldChange = (condIndex, field, value) => {
    const conditions = [...(localCond.conditions || [])];
    conditions[condIndex] = { ...conditions[condIndex], [field]: value };
    handleChange('conditions', conditions);
  };

  const addConditionField = () => {
    const conditions = [...(localCond.conditions || []), { attr: '', op: '=', value: '' }];
    handleChange('conditions', conditions);
  };

  const removeConditionField = (condIndex) => {
    const conditions = (localCond.conditions || []).filter((_, i) => i !== condIndex);
    handleChange('conditions', conditions);
  };

  const isAlwaysTrue = !localCond.conditions || localCond.conditions.length === 0;

  return (
    <Box sx={{
      border: '1px solid #ddd',
      borderRadius: 1,
      p: 1.5,
      mb: 1,
      bgcolor: isAlwaysTrue ? '#f0fff0' : '#fff',
      position: 'relative'
    }}>
      {/* Priority badge */}
      <Box sx={{
        position: 'absolute',
        top: -10,
        left: 10,
        bgcolor: '#1976d2',
        color: 'white',
        px: 1,
        borderRadius: 1,
        fontSize: '0.75rem',
        fontWeight: 'bold'
      }}>
        #{index + 1}
      </Box>

      {/* Move/Delete buttons */}
      <Box sx={{ position: 'absolute', top: 5, right: 5 }}>
        <Button size="small" disabled={isFirst} onClick={() => onMoveUp(index)} sx={{ minWidth: 30 }}>↑</Button>
        <Button size="small" disabled={isLast} onClick={() => onMoveDown(index)} sx={{ minWidth: 30 }}>↓</Button>
        <Button size="small" color="error" onClick={() => onDelete(index)} sx={{ minWidth: 30 }}>×</Button>
      </Box>

      <Stack spacing={1.5} sx={{ mt: 1 }}>
        {/* IF Section */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2' }}>IF:</Typography>
          {isAlwaysTrue ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontStyle: 'italic' }}>
                Always (fallback - no conditions)
              </Typography>
              <Button size="small" variant="outlined" onClick={addConditionField}>
                + Add Condition
              </Button>
            </Box>
          ) : (
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {(localCond.conditions || []).map((cond, condIndex) => {
                const selectedAttr = attributeLabels.find(a => a.value === cond.attr);
                const hasInvalidAttr = cond.attr && !selectedAttr;
                return (
                  <Stack key={condIndex} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {condIndex > 0 && (
                      <Typography variant="caption" sx={{ color: '#666', minWidth: 35 }}>AND</Typography>
                    )}
                    <Autocomplete
                      size="small"
                      options={attributeLabels}
                      groupBy={(option) => option.category || 'general'}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                      value={selectedAttr || null}
                      onChange={(e, newValue) => {
                        handleConditionFieldChange(condIndex, 'attr', newValue?.value || '');
                      }}
                      filterOptions={(options, { inputValue }) => {
                        const filterText = inputValue.toLowerCase();
                        return options.filter(option =>
                          option.label.toLowerCase().includes(filterText)
                        );
                      }}
                      isOptionEqualToValue={(option, value) => option?.value === value?.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type to search attribute..."
                          error={hasInvalidAttr}
                          helperText={hasInvalidAttr ? `Unknown: ${cond.attr}` : ''}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                          <Box>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" sx={{ color: '#888' }}>{option.value}</Typography>
                          </Box>
                        </li>
                      )}
                      sx={{ width: 280 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={cond.op || '='}
                        onChange={(e) => handleConditionFieldChange(condIndex, 'op', e.target.value)}
                      >
                        {CONDITION_OPERATORS.map(o => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      placeholder="Value"
                      value={cond.value || ''}
                      onChange={(e) => handleConditionFieldChange(condIndex, 'value', e.target.value)}
                      sx={{ width: 140 }}
                    />
                    <Button size="small" color="error" onClick={() => removeConditionField(condIndex)} sx={{ minWidth: 30 }}>×</Button>
                  </Stack>
                );
              })}
              <Button size="small" variant="text" onClick={addConditionField} sx={{ alignSelf: 'flex-start' }}>
                + AND
              </Button>
            </Stack>
          )}
        </Box>

        {/* THEN Section */}
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>THEN:</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={localCond.actionType || 'table'}
                onChange={(e) => handleChange('actionType', e.target.value)}
              >
                {ACTION_TYPES.map(a => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {localCond.actionType === 'table' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={localCond.tableId || ''}
                  onChange={(e) => handleChange('tableId', e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Select Table</MenuItem>
                  {priceTables.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name || t.code}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {localCond.actionType === 'formula' && (
              <>
                <TextField
                  size="small"
                  placeholder="e.g., W*H/144"
                  value={localCond.formula || ''}
                  onChange={(e) => handleChange('formula', e.target.value)}
                  sx={{ width: 150 }}
                />
                <Typography variant="caption">×</Typography>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Unit $"
                  value={localCond.unitPrice || ''}
                  onChange={(e) => handleChange('unitPrice', e.target.value)}
                  sx={{ width: 80 }}
                />
              </>
            )}

            {localCond.actionType === 'surcharge' && (
              <>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Amount"
                  value={localCond.surchargeAmount || ''}
                  onChange={(e) => handleChange('surchargeAmount', e.target.value)}
                  sx={{ width: 100 }}
                  InputProps={{ startAdornment: <span>$</span> }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={localCond.surchargeType || 'fixed'}
                    onChange={(e) => handleChange('surchargeType', e.target.value)}
                  >
                    <MenuItem value="fixed">Fixed</MenuItem>
                    <MenuItem value="per_sqft">Per SqFt</MenuItem>
                    <MenuItem value="per_linear_ft">Per Linear Ft</MenuItem>
                    <MenuItem value="percentage">Percentage</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        </Box>

        {/* Description */}
        <TextField
          size="small"
          placeholder="Description (optional)"
          value={localCond.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          fullWidth
        />
      </Stack>
    </Box>
  );
};

// Main Condition Builder component
const ConditionBuilder = ({ conditions, priceTables, attributeLabels, onChange }) => {
  const handleUpdate = (index, updated) => {
    const newConditions = [...conditions];
    newConditions[index] = updated;
    onChange(newConditions);
  };

  const handleDelete = (index) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    onChange(newConditions);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newConditions = [...conditions];
    [newConditions[index - 1], newConditions[index]] = [newConditions[index], newConditions[index - 1]];
    onChange(newConditions);
  };

  const handleMoveDown = (index) => {
    if (index === conditions.length - 1) return;
    const newConditions = [...conditions];
    [newConditions[index], newConditions[index + 1]] = [newConditions[index + 1], newConditions[index]];
    onChange(newConditions);
  };

  const handleAdd = () => {
    const newCondition = {
      conditions: [],
      actionType: 'table',
      tableId: '',
      description: ''
    };
    onChange([...conditions, newCondition]);
  };

  return (
    <Box>
      <Box sx={{ p: 1, mb: 1, bgcolor: '#e3f2fd', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ color: '#1565c0' }}>
          Conditions are evaluated in order. First matching condition wins.
          Empty conditions (no IF rules) act as a fallback.
        </Typography>
      </Box>

      {conditions.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary">
            No conditions defined. Click "Add Condition" to start.
          </Typography>
        </Box>
      ) : (
        conditions.map((cond, index) => (
          <ConditionRow
            key={index}
            condition={cond}
            index={index}
            priceTables={priceTables}
            attributeLabels={attributeLabels}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            isFirst={index === 0}
            isLast={index === conditions.length - 1}
          />
        ))
      )}

      <Button
        variant="outlined"
        onClick={handleAdd}
        sx={{ mt: 1 }}
      >
        + Add Condition
      </Button>
    </Box>
  );
};

// Main Pricing Rules Tab
const PricingRulesTab = ({ highlightRule = '' }) => {
  const [rules, setRules] = useState([]);
  const [priceTables, setPriceTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const { attributeLabels } = useAttributeLabels();
  const { canSeeScope } = useProductScope();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rulesData, tablesData] = await Promise.all([
        authFetch('/pricing/rules'),
        authFetch('/pricing/tables')
      ]);
      setRules(rulesData);
      setPriceTables(tablesData);
      // Auto-select and scroll to the highlighted rule after data loads
      if (highlightRule) {
        const match = rulesData.find(r => r.code === highlightRule);
        if (match) {
          setSelectedRow(match);
          // Find the page this row is on and navigate to it
          const idx = rulesData.indexOf(match);
          setPaginationModel(prev => ({ ...prev, page: Math.floor(idx / prev.pageSize) }));
          // Scroll after the grid has rendered
          setTimeout(() => {
            const el = document.querySelector(`[data-id="${match.id}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      const { conditions, ...ruleData } = formData;

      if (editData?.id) {
        updatedRow = await authFetchRequest(`/pricing/rules/${editData.id}`, ruleData, 'PUT');

        if (conditions && conditions.length > 0) {
          const conditionsToSave = conditions.map((cond, idx) => ({
            priority: idx + 1,
            conditions: cond.conditions || [],
            action_type: cond.actionType || 'table',
            pricing_table_id: cond.tableId || null,
            formula_expr: cond.formula || null,
            formula_unit_price: cond.unitPrice || 1.0,
            surcharge_amount: cond.surchargeAmount || null,
            surcharge_type: cond.surchargeType || 'fixed',
            description: cond.description || null,
            active: true
          }));
          await authFetchRequest(`/pricing/rules/${editData.id}/conditions/batch`, { conditions: conditionsToSave }, 'PUT');
        } else {
          await authFetchRequest(`/pricing/rules/${editData.id}/conditions/batch`, { conditions: [] }, 'PUT');
        }

        const updatedWithConditions = await authFetch(`/pricing/rules/${editData.id}`);
        const ruleConditions = await authFetch(`/pricing/rules/${editData.id}/conditions`);
        updatedWithConditions.conditions = ruleConditions;

        setRules(prev => prev.map(row => row.id === editData.id ? updatedWithConditions : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedWithConditions);
      } else {
        updatedRow = await authFetchRequest('/pricing/rules', ruleData, 'POST');

        if (conditions && conditions.length > 0) {
          const conditionsToSave = conditions.map((cond, idx) => ({
            priority: idx + 1,
            conditions: cond.conditions || [],
            action_type: cond.actionType || 'table',
            pricing_table_id: cond.tableId || null,
            formula_expr: cond.formula || null,
            formula_unit_price: cond.unitPrice || 1.0,
            surcharge_amount: cond.surchargeAmount || null,
            surcharge_type: cond.surchargeType || 'fixed',
            description: cond.description || null,
            active: true
          }));
          await authFetchRequest(`/pricing/rules/${updatedRow.id}/conditions/batch`, { conditions: conditionsToSave }, 'PUT');
        }

        const ruleConditions = await authFetch(`/pricing/rules/${updatedRow.id}/conditions`);
        updatedRow.conditions = ruleConditions;

        setRules(prev => [...prev, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save pricing rule');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this pricing rule?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/rules/${selectedRow.id}`, null, 'DELETE');
      setRules(prev => prev.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete pricing rule');
    }
  };

  const tableNameMap = useMemo(() => {
    const map = {};
    priceTables.forEach(t => { map[t.id] = t.name || t.code; });
    return map;
  }, [priceTables]);

  const attrLabelMap = useMemo(() => {
    const map = {};
    attributeLabels.forEach(a => { map[a.value] = a.label; });
    return map;
  }, [attributeLabels]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 50 },
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'value_expr', headerName: 'Value Expr', width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        const label = attrLabelMap[params.value];
        if (label) return label;
        return <span style={{ color: '#d32f2f', fontStyle: 'italic' }} title={`Unknown: ${params.value}`}>? {params.value}</span>;
      }
    },
    { field: 'column_expr', headerName: 'Column Expr', width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        const label = attrLabelMap[params.value];
        if (label) return label;
        return <span style={{ color: '#d32f2f', fontStyle: 'italic' }} title={`Unknown: ${params.value}`}>? {params.value}</span>;
      }
    },
    { field: 'conditions_count', headerName: 'Conditions', width: 90,
      renderCell: (params) => {
        const count = params.row.conditions?.length || 0;
        return count > 0 ? `${count} rules` : '-';
      }
    },
    { field: 'priority', headerName: 'Priority', width: 70 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Pricing Rules</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Rule
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={async () => {
          if (!selectedRow) return;
          try {
            const conditions = await authFetch(`/pricing/rules/${selectedRow.id}/conditions`);
            const uiConditions = conditions.map(c => ({
              conditions: c.conditions || [],
              actionType: c.action_type,
              tableId: c.pricing_table_id,
              formula: c.formula_expr,
              unitPrice: c.formula_unit_price,
              surchargeAmount: c.surcharge_amount,
              surchargeType: c.surcharge_type,
              description: c.description
            }));
            setEditData({ ...selectedRow, conditions: uiConditions });
            setOpenDialog(true);
          } catch (err) {
            console.error('Failed to fetch conditions:', err);
            setEditData({ ...selectedRow, conditions: [] });
            setOpenDialog(true);
          }
        }} disabled={!selectedRow}>
          Edit Rule
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={handleDelete} disabled={!selectedRow}>
          Delete Rule
        </Button>
      </Stack>

      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <span style={{ fontSize: '0.85em', color: '#555' }}>
          <strong>Pricing rules</strong> define how prices are calculated. Each rule has conditions (IF) and actions (THEN).
          Conditions are evaluated in order - first match wins.
        </span>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rules.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
          getRowClassName={(params) => params.row.id === selectedRow?.id ? 'Mui-selected' : ''}
        />
      )}

      <PricingRuleDialog
        open={openDialog}
        data={editData}
        priceTables={priceTables}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const PricingRuleDialog = ({ open, data, priceTables, onClose, onSave }) => {
  const { pickDefault, optionsForValue } = useProductScope();
  const [lookups1D, setLookups1D] = useState([]);
  const [lookups2D, setLookups2D] = useState([]);
  const [showDiagram, setShowDiagram] = useState(false);
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [formData, setFormData] = useState({
    code: '', name: '', description: '',
    value_expr: '', column_expr: '',
    applies_to: 'all', priority: 0, active: true,
    conditions: [],
    surcharge: null
  });
  const { attributeLabels } = useAttributeLabels(formData.applies_to);

  const validateAttributes = () => {
    const invalid = new Set();
    const knownValues = new Set(attributeLabels.map(a => a.value));

    if (!formData.value_expr) {
      invalid.add('value_expr');
    } else if (!knownValues.has(formData.value_expr)) {
      invalid.add('value_expr');
    }

    if (formData.column_expr && !knownValues.has(formData.column_expr)) {
      invalid.add('column_expr');
    }

    if (formData.surcharge) {
      if (formData.surcharge.key1Attr && !knownValues.has(formData.surcharge.key1Attr)) {
        invalid.add('surcharge-key1');
      }
      if (formData.surcharge.key2Attr && !knownValues.has(formData.surcharge.key2Attr)) {
        invalid.add('surcharge-key2');
      }
    }

    setInvalidFields(invalid);
  };

  useEffect(() => {
    authFetch('/pricing/lookups').then(data => setLookups1D(data || [])).catch(() => setLookups1D([]));
    authFetch('/pricing/lookups-2d').then(data => setLookups2D(data || [])).catch(() => setLookups2D([]));
  }, []);

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        value_expr: data.value_expr || '',
        column_expr: data.column_expr || '',
        applies_to: data.applies_to || pickDefault('all'),
        priority: data.priority || 0,
        active: data.active ?? true,
        conditions: data.conditions || [],
        surcharge: data.surcharge || null
      });
    } else {
      setFormData({
        code: '', name: '', description: '',
        value_expr: '', column_expr: '',
        applies_to: pickDefault('all'), priority: 0, active: true,
        conditions: [],
        surcharge: null
      });
    }
  }, [data, open]);

  const updateSurcharge = (field, value) => {
    const newSurcharge = { ...(formData.surcharge || { condition: 'result > 0', type: 'fixed', value: 0 }), [field]: value };
    if (field === 'type') {
      if (value === 'fixed') {
        newSurcharge.lookupId = null;
        newSurcharge.key1Attr = '';
        newSurcharge.key2Attr = '';
      } else if (value === '1d') {
        newSurcharge.key2Attr = '';
      }
    }
    setFormData({ ...formData, surcharge: newSurcharge });
  };
  const clearSurcharge = () => setFormData({ ...formData, surcharge: null });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Pricing Rule' : 'Add Pricing Rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Basic Info */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              sx={{ width: 150 }}
              placeholder="e.g., w1"
            />
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              placeholder="e.g., Window Section 1 Base Price"
            />
          </Stack>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />

          {/* Expressions */}
          <Typography variant="subtitle2" sx={{ mt: 1, mb: -1 }}>Lookup Expressions</Typography>
          <Box sx={{ p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1, fontSize: '0.8rem', color: '#666' }}>
            <strong>Value Expression:</strong> What to look up in the row (e.g., Square of the first section) &nbsp;|&nbsp;
            <strong>Column Expression:</strong> What to look up in the column (e.g., Type of the first section)
          </Box>
          <Stack direction="row" spacing={2}>
            <Autocomplete
              size="small"
              options={attributeLabels}
              groupBy={(option) => option.category || 'general'}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={attributeLabels.find(a => a.value === formData.value_expr) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, value_expr: newValue?.value || '' });
              }}
              filterOptions={(options, { inputValue }) => {
                const filterText = inputValue.toLowerCase();
                return options.filter(option =>
                  option.label.toLowerCase().includes(filterText)
                );
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => {
                const hasInvalid = invalidFields.has('value_expr') || (formData.value_expr && !attributeLabels.find(a => a.value === formData.value_expr));
                return (
                  <TextField
                    {...params}
                    label="Value Expression (Row lookup)"
                    placeholder="Type to search..."
                    error={hasInvalid}
                    helperText={hasInvalid ? `Unknown: ${formData.value_expr}` : ''}
                  />
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>{option.value}</Typography>
                  </Box>
                </li>
              )}
              sx={{ flex: 1, ...(invalidFields.has('value_expr') && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
              fullWidth
            />
            <Autocomplete
              size="small"
              options={attributeLabels}
              groupBy={(option) => option.category || 'general'}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
              value={attributeLabels.find(a => a.value === formData.column_expr) || null}
              onChange={(_, newValue) => {
                setFormData({ ...formData, column_expr: newValue?.value || '' });
              }}
              filterOptions={(options, { inputValue }) => {
                const filterText = inputValue.toLowerCase();
                return options.filter(option =>
                  option.label.toLowerCase().includes(filterText)
                );
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => {
                const hasInvalid = invalidFields.has('column_expr') || (formData.column_expr && !attributeLabels.find(a => a.value === formData.column_expr));
                return (
                  <TextField
                    {...params}
                    label="Column Expression"
                    placeholder="Type to search..."
                    error={hasInvalid}
                    helperText={hasInvalid ? `Unknown: ${formData.column_expr}` : ''}
                  />
                );
              }}
              sx={{ flex: 1, ...(invalidFields.has('column_expr') && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" sx={{ color: '#888' }}>{option.value}</Typography>
                  </Box>
                </li>
              )}
              fullWidth
            />
          </Stack>

          {/* Conditions Builder */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0 }}>
            Conditions (IF → THEN)
          </Typography>
          <ConditionBuilder
            conditions={formData.conditions}
            priceTables={priceTables}
            attributeLabels={attributeLabels}
            onChange={(conditions) => setFormData({ ...formData, conditions })}
          />

          {/* Surcharge Section */}
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#fff3e0', mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                Surcharge (optional - add to result)
              </Typography>
              {formData.surcharge ? (
                <Button size="small" color="error" onClick={clearSurcharge}>Remove Surcharge</Button>
              ) : (
                <Button size="small" variant="outlined" color="warning" onClick={() => updateSurcharge('type', 'fixed')}>+ Add Surcharge</Button>
              )}
            </Stack>

            {formData.surcharge ? (
              <Stack spacing={2}>
                <TextField
                  size="small"
                  label="Condition"
                  value={formData.surcharge.condition || ''}
                  onChange={(e) => updateSurcharge('condition', e.target.value)}
                  placeholder="e.g., result > 0"
                  helperText="When to add the surcharge (has access to 'result' variable)"
                  fullWidth
                  sx={{ '& input': { fontFamily: 'monospace' } }}
                />
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={formData.surcharge.type || 'fixed'} onChange={(e) => updateSurcharge('type', e.target.value)} label="Type">
                      <MenuItem value="fixed">Fixed Value</MenuItem>
                      <MenuItem value="1d">1D Lookup</MenuItem>
                      <MenuItem value="2d">2D Lookup</MenuItem>
                    </Select>
                  </FormControl>

                  {formData.surcharge.type === 'fixed' && (
                    <TextField
                      size="small"
                      type="number"
                      label="Value"
                      value={formData.surcharge.value || 0}
                      onChange={(e) => updateSurcharge('value', parseFloat(e.target.value) || 0)}
                      sx={{ width: 120 }}
                      inputProps={{ step: 0.01 }}
                    />
                  )}

                  {(formData.surcharge.type === '1d' || formData.surcharge.type === '2d') && (
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Lookup Table</InputLabel>
                      <Select
                        value={formData.surcharge.lookupId || ''}
                        onChange={(e) => updateSurcharge('lookupId', e.target.value)}
                        label="Lookup Table"
                      >
                        {(formData.surcharge.type === '2d' ? lookups2D : lookups1D).map(l => (
                          <MenuItem key={l.id} value={l.id}>{l.name || l.code}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>

                {(formData.surcharge.type === '1d' || formData.surcharge.type === '2d') && (
                  <Stack direction="row" spacing={2}>
                    <Autocomplete
                      size="small"
                      options={attributeLabels}
                      groupBy={(option) => option.category || 'general'}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                      value={attributeLabels.find(a => a.value === formData.surcharge.key1Attr) || null}
                      onChange={(_, newValue) => updateSurcharge('key1Attr', newValue?.value || '')}
                      filterOptions={(options, { inputValue }) => {
                        const filterText = inputValue.toLowerCase();
                        return options.filter(option =>
                          option.label?.toLowerCase().includes(filterText) ||
                          option.value?.toLowerCase().includes(filterText)
                        );
                      }}
                      renderInput={(params) => <TextField {...params} label={formData.surcharge.type === '2d' ? 'Row Key' : 'Lookup Key'} placeholder="Search attributes..." error={invalidFields.has('surcharge-key1')} />}
                      sx={{ flex: 1, ...(invalidFields.has('surcharge-key1') && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                      freeSolo
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') updateSurcharge('key1Attr', value);
                      }}
                    />
                    {formData.surcharge.type === '2d' && (
                      <Autocomplete
                        size="small"
                        options={attributeLabels}
                        groupBy={(option) => option.category || 'general'}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                        value={attributeLabels.find(a => a.value === formData.surcharge.key2Attr) || null}
                        onChange={(_, newValue) => updateSurcharge('key2Attr', newValue?.value || '')}
                        filterOptions={(options, { inputValue }) => {
                          const filterText = inputValue.toLowerCase();
                          return options.filter(option =>
                            option.label?.toLowerCase().includes(filterText) ||
                            option.value?.toLowerCase().includes(filterText)
                          );
                        }}
                        renderInput={(params) => <TextField {...params} label="Column Key" placeholder="Search attributes..." error={invalidFields.has('surcharge-key2')} />}
                        sx={{ flex: 1, ...(invalidFields.has('surcharge-key2') && { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 } } }) }}
                        freeSolo
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') updateSurcharge('key2Attr', value);
                        }}
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                No surcharge. Click "+ Add Surcharge" to add an optional amount to the result.
              </Typography>
            )}
          </Box>

          {/* Meta */}
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel>Applies To</InputLabel>
              <Select
                value={formData.applies_to}
                onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
                label="Applies To"
              >
                {optionsForValue(formData.applies_to).map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              sx={{ width: 120 }}
              helperText="Higher = first"
            />
            <FormControlLabel
              control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" onClick={() => setShowDiagram(true)}>Diagram</Button>
        <Button variant="outlined" color="warning" onClick={validateAttributes}>Validate</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
      <PricingRuleDiagram
        open={showDiagram}
        onClose={() => setShowDiagram(false)}
        conditions={formData.conditions}
        ruleName={formData.name || formData.code}
        priceTables={priceTables}
        surcharge={formData.surcharge}
      />
    </Dialog>
  );
};

export default PricingRulesTab;
